import { useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Col, Input, Label, Row, Spinner } from 'reactstrap';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { ImageWithFallback } from 'Components/pos2u/ImageWithFallback';
import { uploadSettingsLogo } from 'services/settingsService';
import { useSettings } from 'pages/SettingsV2/SettingsContext';
import { TEMPLATE_OPTIONS } from 'pages/SettingsV2/SettingsConstants';
import {
  DocumentTemplateType,
  SettingsFormValues,
} from 'pages/SettingsV2/settingsTypes';
import SettingsV2SectionCard from 'pages/SettingsV2/SettingsV2SectionCard';

interface LogoUploadProps {
  label: string;
  helperText: string;
  value: string;
  onUploaded: (fileName: string) => void;
  onRemoved: () => void;
}

const LogoUploadV2 = ({
  label,
  helperText,
  value,
  onUploaded,
  onRemoved,
}: LogoUploadProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const shownSrc = preview ?? (value.includes('/') ? value : '');

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const res: any = await uploadSettingsLogo(file);
      if (res?.error_code === 0 && res.data?.file_name) {
        onUploaded(res.data.file_name);
      } else {
        setPreview(null);
        toast.error(t(res?.message || 'Failed to upload logo'));
      }
    } catch (err: any) {
      setPreview(null);
      toast.error(t(err?.message || 'Failed to upload logo'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="form-label mb-2">{label}</Label>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="d-flex flex-column align-items-center justify-content-center border border-dashed rounded w-100 bg-light-subtle text-muted"
        style={{
          minHeight: 200,
          padding: shownSrc ? '28px 24px' : '20px 16px',
          cursor: 'pointer',
        }}
      >
        {uploading ? (
          <Spinner color="primary" size="sm" />
        ) : shownSrc ? (
          <div
            className="d-inline-flex position-relative align-items-center justify-content-center"
            style={{ minHeight: 120, maxWidth: '100%' }}
          >
            <ImageWithFallback
              src={shownSrc}
              alt={label}
              style={{
                maxHeight: 120,
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
            <button
              type="button"
              aria-label={t('Remove image')}
              className="position-absolute border-0"
              style={{
                top: '-12px',
                right: '-12px',
                backgroundColor: 'red',
                borderRadius: '50%',
                width: '1.5rem',
                height: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setPreview(null);
                onRemoved();
              }}
            >
              <i
                className="ri-close-fill"
                style={{ color: 'white', fontSize: '1.25rem' }}
              />
            </button>
          </div>
        ) : (
          <div className="text-center py-2">
            <FaCloudUploadAlt size={28} />
            <div className="fs-13 mt-2">{t('Click to upload')}</div>
            <div className="fs-12 mt-1">{helperText}</div>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFile}
      />
    </div>
  );
};

const DocumentSectionV2 = () => {
  const { t } = useTranslation();
  const { flags } = useSettings();
  const { values, setFieldValue, handleChange, handleBlur } =
    useFormikContext<SettingsFormValues>();
  const { companyLogoUrl, receiptLogoUrl, templateType } = values.document;

  return (
    <SettingsV2SectionCard
      sectionKey="document"
      title={t('Document Settings')}
      icon="ri-file-text-line"
    >
      <div className="mb-3 row">
        <Label className="col-sm-3 col-form-label pt-sm-0">
          {t('Logo Images')}
        </Label>
        <Col sm={9}>
          <Row className="gy-3">
            <Col md={6}>
              <LogoUploadV2
                label={t('Company Logo')}
                helperText={t('Size 250x250(px)')}
                value={companyLogoUrl}
                onUploaded={(fileName) =>
                  setFieldValue('document.companyLogoUrl', fileName)
                }
                onRemoved={() => setFieldValue('document.companyLogoUrl', '')}
              />
            </Col>
            <Col md={6}>
              <LogoUploadV2
                label={t('Receipt Logo')}
                helperText={t('Size 100x100(px)')}
                value={receiptLogoUrl}
                onUploaded={(fileName) =>
                  setFieldValue('document.receiptLogoUrl', fileName)
                }
                onRemoved={() => setFieldValue('document.receiptLogoUrl', '')}
              />
            </Col>
          </Row>
          <div className="text-muted fs-13 mt-2">
            {t('Logo images should be black and white')}
          </div>
          <div className="text-muted fs-13 mt-1">
            {t('Supported files: gif, png, jpg, jpeg')}
          </div>
        </Col>
      </div>

      {flags.show_doc_template ? (
        <div className="mb-3 row">
          <Label className="col-sm-3 col-form-label pt-sm-0">
            {t('Document Template')}
          </Label>
          <Col sm={9}>
            <div className="d-flex gap-3 flex-wrap">
              {TEMPLATE_OPTIONS.map((template) => {
                const selected = templateType === template.value;
                return (
                  <button
                    type="button"
                    key={template.value}
                    onClick={() =>
                      setFieldValue(
                        'document.templateType',
                        template.value as DocumentTemplateType
                      )
                    }
                    onDoubleClick={() =>
                      window.open(
                        template.image,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    title={t('Double-click to open in new tab')}
                    className={`btn p-2 border rounded ${
                      selected ? 'border-primary border-2' : 'border-light'
                    }`}
                    style={{ width: 170 }}
                  >
                    <ImageWithFallback
                      src={template.image}
                      alt={t(template.labelKey)}
                      style={{
                        width: '100%',
                        height: 110,
                        objectFit: 'contain',
                      }}
                    />
                    <div className="text-center fs-13 mt-2">
                      {t(template.labelKey)}
                    </div>
                  </button>
                );
              })}
            </div>
          </Col>
        </div>
      ) : null}

      <div className="mb-3 row">
        <Label
          htmlFor="document-v2-receipt-comment"
          className="col-sm-3 col-form-label"
        >
          {t('Receipt / Tax Invoice Note')}
        </Label>
        <Col sm={9}>
          <Input
            id="document-v2-receipt-comment"
            type="textarea"
            rows={4}
            name="document.receiptComment"
            value={values.document.receiptComment}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={500}
          />
        </Col>
      </div>

      {flags.show_quotation ? (
        <div className="row">
          <Label
            htmlFor="document-v2-quotation-comment"
            className="col-sm-3 col-form-label"
          >
            {t('Quotation Note')}
          </Label>
          <Col sm={9}>
            <Input
              id="document-v2-quotation-comment"
              type="textarea"
              rows={4}
              name="document.quotationComment"
              value={values.document.quotationComment}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={500}
            />
          </Col>
        </div>
      ) : null}
    </SettingsV2SectionCard>
  );
};

export default DocumentSectionV2;
